import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { User, Mail, Hash, MapPin, Calendar, Settings, Shield, ChevronRight, Award } from "lucide-react";

export default function Profile() {
  const { user, member, isMember, isAdmin, logout } = useAuth();
  const { data } = useQuery({ queryKey: ["profile"], queryFn: () => api.get("/member/profile") });
  const profile = data?.member || member;

  return (
    <div className="max-w-lg" data-testid="profile-page">
      <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide mb-8">Profile</h1>
      <div className="space-y-6">
        <div className="bg-walnut border border-lv-border p-5 sm:p-6 space-y-4">
          <h2 className="text-xs tracking-[0.3em] uppercase text-lv-muted mb-3">Account</h2>
          <div className="flex items-center gap-3">
            <Mail size={16} className="text-bronze shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Email</p>
              <p className="text-sm text-cream truncate" data-testid="profile-email">{user?.email}</p>
            </div>
          </div>
        </div>
        {profile && (
          <div className="bg-walnut border border-lv-border p-5 sm:p-6 space-y-4">
            <h2 className="text-xs tracking-[0.3em] uppercase text-lv-muted mb-3">Membership</h2>
            <div className="flex items-center gap-3">
              <User size={16} className="text-bronze shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Full Name</p>
                <p className="text-sm text-cream truncate" data-testid="profile-name">{profile.full_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Hash size={16} className="text-bronze shrink-0" />
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Member ID</p>
                <p className="text-sm text-cream font-mono" data-testid="profile-code">{profile.member_code}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin size={16} className="text-bronze shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Chapter</p>
                <p className="text-sm text-cream truncate" data-testid="profile-chapter">{profile.home_chapter_name || "\u2014"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar size={16} className="text-bronze shrink-0" />
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Member Since</p>
                <p className="text-sm text-cream" data-testid="profile-since">
                  {profile.member_since ? new Date(profile.member_since).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "\u2014"}
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Member navigation links */}
        {isMember && !isAdmin && (
          <div className="space-y-2">
            <Link
              to="/privileges"
              className="flex items-center justify-between bg-walnut border border-lv-border p-4 group hover:border-bronze/40 transition-colors"
              data-testid="profile-privileges-link"
            >
              <div className="flex items-center gap-3">
                <Award size={16} className="text-bronze shrink-0" />
                <span className="text-sm text-cream tracking-wide">My Privileges</span>
              </div>
              <ChevronRight size={16} className="text-lv-muted/40 group-hover:text-bronze transition-colors" />
            </Link>
            <Link
              to="/preferences"
              className="flex items-center justify-between bg-walnut border border-lv-border p-4 group hover:border-bronze/40 transition-colors"
              data-testid="profile-preferences-link"
            >
              <div className="flex items-center gap-3">
                <Settings size={16} className="text-bronze shrink-0" />
                <span className="text-sm text-cream tracking-wide">Preferences</span>
              </div>
              <ChevronRight size={16} className="text-lv-muted/40 group-hover:text-bronze transition-colors" />
            </Link>
            <Link
              to="/privacy"
              className="flex items-center justify-between bg-walnut border border-lv-border p-4 group hover:border-bronze/40 transition-colors"
              data-testid="profile-privacy-link"
            >
              <div className="flex items-center gap-3">
                <Shield size={16} className="text-bronze shrink-0" />
                <span className="text-sm text-cream tracking-wide">Privacy & Permissions</span>
              </div>
              <ChevronRight size={16} className="text-lv-muted/40 group-hover:text-bronze transition-colors" />
            </Link>
          </div>
        )}
        <button onClick={logout} className="lv-btn-secondary w-full min-h-[48px]" data-testid="profile-logout">Sign Out</button>
      </div>
    </div>
  );
}
